import { useMemo, useState } from 'react';
import type { CapabilityRegister, DesignationStatus, ScenarioInput, SpecStatus } from '@exigo/shared';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Select } from '../../components/ui/Select';
import { Switch } from '../../components/ui/Switch';
import type { ReferenceData } from '../../api/types';
import styles from './CapabilityPanel.module.css';
import calcStyles from './CalculatorPage.module.css';

interface CapabilityPanelProps {
  input: ScenarioInput;
  setInput: (updater: (prev: ScenarioInput) => ScenarioInput) => void;
  reference: ReferenceData;
}

const SPEC_STATUS_OPTIONS = [
  { value: 'enrolled', label: 'Enrolled' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'progress', label: 'In progress' },
  { value: 'none', label: 'Not started' },
];

const DESIGNATION_STATUS_OPTIONS = [
  { value: 'enrolled', label: 'Enrolled' },
  { value: 'progress', label: 'In progress' },
  { value: 'none', label: 'Not started' },
];

const GROUP_LABELS: Record<string, string> = {
  azure: 'Azure',
  modernwork: 'Modern Work',
  security: 'Security',
  bizapps: 'Business Applications',
};

/**
 * The partner capability register — which specializations and designations
 * Exigo holds. Feeds the "Partner capability" gate on every engagement row.
 */
export function CapabilityPanel({ input, setInput, reference }: CapabilityPanelProps): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);

  const enrolledSpecCount = useMemo(
    () => Object.values(input.capability.specs).filter((s) => s === 'enrolled').length,
    [input.capability.specs],
  );
  const enrolledDesignationCount = useMemo(
    () => Object.values(input.capability.designations).filter((s) => s === 'enrolled').length,
    [input.capability.designations],
  );

  const specGroups = useMemo(() => {
    const groups = new Map<string, typeof reference.specializations>();
    for (const spec of reference.specializations) {
      const list = groups.get(spec.group) ?? [];
      list.push(spec);
      groups.set(spec.group, list);
    }
    return groups;
  }, [reference]);

  function setSpecStatus(key: string, status: string): void {
    setInput((p) => updateCapability(p, (c) => ({ specs: { ...c.specs, [key]: status as SpecStatus } })));
  }

  function setDesignationStatus(key: string, status: string): void {
    setInput((p) =>
      updateCapability(p, (c) => ({ designations: { ...c.designations, [key]: status as DesignationStatus } })),
    );
  }

  /**
   * Restores the register to the guide's recorded position for Exigo. The two
   * what-if switches are cleared with it: they exist to answer "what would we
   * qualify for if…", and leaving one on after a reset would quietly keep that
   * hypothetical in force.
   */
  function resetToCurrentPosition(): void {
    setInput((p) => ({
      ...p,
      capability: {
        specs: { ...reference.defaults.specs },
        designations: { ...reference.defaults.designations },
        countQualified: false,
        isvPaired: false,
      },
    }));
  }

  return (
    <Card className={calcStyles.section}>
      <div
        className={styles.headerRow}
        role="button"
        tabIndex={0}
        onClick={() => { setExpanded((v) => !v); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setExpanded((v) => !v);
        }}
        aria-expanded={expanded}
      >
        <p className={calcStyles.sectionTitle} style={{ marginBottom: 0 }}>
          Partner capability register
        </p>
        <span className={styles.summary}>
          {enrolledSpecCount} specialization{enrolledSpecCount === 1 ? '' : 's'} ·{' '}
          {enrolledDesignationCount} designation{enrolledDesignationCount === 1 ? '' : 's'} enrolled ·{' '}
          {expanded ? 'Hide' : 'Edit'}
        </span>
      </div>

      {expanded && (
        <>
          <div className={styles.toggleRow}>
            <Switch
              label="Treat 'Qualified' as eligible"
              checked={input.capability.countQualified}
              onChange={(v) => { setInput((p) => updateCapability(p, () => ({ countQualified: v }))); }}
            />
            <Switch
              label="Paired ISV partner confirmed"
              checked={input.capability.isvPaired}
              onChange={(v) => { setInput((p) => updateCapability(p, () => ({ isvPaired: v }))); }}
            />
            <Button size="sm" onClick={resetToCurrentPosition}>
              Reset to current position
            </Button>
          </div>

          <p className={styles.note}>
            &ldquo;Treat Qualified as eligible&rdquo; is off by default. <b>Qualified</b> means the
            prerequisites are met but enrolment and audit are incomplete — Partner Center checks
            generally read enrolled status only. <b>Reset to current position</b> restores the
            register to Exigo&rsquo;s recorded position in this guide edition.
          </p>

          <div className={styles.groups}>
            <div>
              <p className={styles.groupTitle}>Designations</p>
              {reference.designations.map((d) => (
                <div className={styles.row} key={d.key}>
                  <span className={styles.rowLabel} title={d.name}>
                    {d.name}
                  </span>
                  <div className={styles.statusSelect}>
                    <Select
                      value={input.capability.designations[d.key] ?? 'none'}
                      onChange={(v) => { setDesignationStatus(d.key, v); }}
                      options={DESIGNATION_STATUS_OPTIONS}
                      ariaLabel={`${d.name} status`}
                    />
                  </div>
                </div>
              ))}
            </div>

            {[...specGroups.entries()].map(([group, specs]) => (
              <div key={group}>
                <p className={styles.groupTitle}>{GROUP_LABELS[group] ?? group}</p>
                {specs.map((s) => (
                  <div className={styles.row} key={s.key}>
                    <span className={styles.rowLabel} title={s.name}>
                      {s.name}
                    </span>
                    <div className={styles.statusSelect}>
                      <Select
                        value={input.capability.specs[s.key] ?? 'none'}
                        onChange={(v) => { setSpecStatus(s.key, v); }}
                        options={SPEC_STATUS_OPTIONS}
                        ariaLabel={`${s.name} status`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

function updateCapability(
  input: ScenarioInput,
  patch: (c: CapabilityRegister) => Partial<CapabilityRegister>,
): ScenarioInput {
  return { ...input, capability: { ...input.capability, ...patch(input.capability) } };
}
